import React, { useState, useCallback } from 'react';
import {
  Collapse,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useIntl } from 'react-intl';

import moment from 'moment';

import { SHeadTableCell } from '../../../../components/styledComponents/Tables';
import { ColorButton } from '../../../../components/other/Buttons';
import { orderTypeList } from '../constatns';
import { ViewMode } from '../orderPage';
import { IUser } from '../../../../interfaces/user';
import { IOrderResponse, IOrderType } from '../../../../interfaces/order';
import { useStyles } from '../hooks/useStyles';
import { useDefineUserRole } from '../../../../hooks';
import AlertDialog from '../../../../components/other/Dialog/AlertDialog';

const getStatusString = (type: string, selectedOrderStatuses: string[]): string => {
  if (type === 'full') {
    if (selectedOrderStatuses.length > 0) {
      return selectedOrderStatuses.toString();
    }
    return orderTypeList.toString();
  }
  if (type === 'abandoned') return 'cart';
  return '';
};

type TProps = {
  item: IOrderResponse;
  me?: IUser;
  type: string;
  selectAction: (id: any) => void;
  selectActionPaid: (id: any) => void;
  editStatus: (id: any) => void;
  handleDeleteDialog: (id: number | undefined) => void;
  editAction: (item: any, mode: any) => void;
  selectedOrderStatuses: string[];
  selectedPaidOrderStatuses: string[];
  selectedId: number;
  selectedPaidId: number;
  isBuyer: boolean;
  translates: { [p: string]: string };
  translatesStatus: { [p: string]: string };
  page: number;
  perPage: number;
  userId: number | undefined;
  orderTypes: IOrderType[];
  orderStatusTypes: IOrderType[];
  isCreateOrder: boolean;
};
const TableRowOrderList: React.FC<TProps> = ({
  item,
  me,
  type,
  selectAction,
  handleDeleteDialog,
  editAction,
  selectedOrderStatuses,
  selectedId,
  isBuyer,
  translates,
  translatesStatus,
  editStatus,
  page,
  perPage,
  userId,
  orderTypes,
  orderStatusTypes,
  selectedPaidId,
  selectActionPaid,
  isCreateOrder
}) => {
  const { classes } = useStyles();
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const isManager = useDefineUserRole(me, 'ROLE_MANAGER');
  const isOperator = useDefineUserRole(me, 'ROLE_ORDER_OPERATOR');
  const [isAlertOpen, setAlertOpen] = useState(false);
  const isEditOrder = useDefineUserRole(me, [
    'ROLE_MANAGER',
    'ROLE_ADMIN',
    'ROLE_ORDER_OPERATOR',
  ]);

  const handleEditStatus = (value: string) => {
    if (value === 'completed') {
      setAlertOpen(true);
    } else {
      editStatus({
        id: item.id,
        page,
        perPage,
        statuses: getStatusString(type, selectedOrderStatuses),
        data: { status: value },
        userId,
        root: me?.is_admin,
      });
      selectAction(-1);
    }
  };

  const completeOrder = () => {
    editStatus({
      id: item.id,
      page,
      perPage,
      statuses: getStatusString(type, selectedOrderStatuses),
      data: { status: 'completed' },
      userId,
      root: me?.is_admin,
    });
    selectAction(-1);
    setAlertOpen(false);
  };

  const filterAddress = useCallback((address: string) => {
    if (!address) return '-';
    const splitAddress = address.replace('Республика Ингушетия,', '').trim();

    return splitAddress || '-';
  }, []);

  const filteredOrderTypes = orderTypes.filter(type =>
    [
      'formed',
      'new',
      'driver_found',
      'driver_coming',
      'driver_waiting',
      'way',
      'completed',
      'canceled',
    ].includes(type.type)
  );

  // const isOpen = me?.is_admin && (item?.orders?.length ?? 0) > 1;
  return (
    <>
      <AlertDialog
        open={isAlertOpen}
        message={intl.formatMessage({ id: 'ORDER.COMPLETE.STATUS.ALERT' })}
        okText={intl.formatMessage({ id: 'CATEGORIES.DELETE.OK' })}
        cancelText={intl.formatMessage({ id: 'CATEGORIES.DELETE.CANCEL' })}
        handleClose={() => {
          setAlertOpen(false);
        }}
        handleAgree={() => completeOrder()}
      />
      <TableRow key={item.id}>
        <TableCell className={classes.cell}>{item.id || '-'} </TableCell>
        <TableCell className={classes.cell}>
          {item.created_at ? moment(item.created_at).format('DD.MM.YYYY HH:mm') : '-'}
        </TableCell>
        <TableCell className={classes.cell}>
          {filterAddress(item.start_location?.address) || '-'}
        </TableCell>
        <TableCell className={classes.cell}>
          {filterAddress(item.end_location?.address) || '-'}
        </TableCell>
        <TableCell className={classes.cell}>{item.comment || '-'}</TableCell>
        <TableCell className={classes.cell}>{item.summ} руб</TableCell>
        <TableCell className={classes.cell}>
          {item.fio || ''} {`${item.user?.phone ? `+${item.user.phone}` : ''}` || '-'}
        </TableCell>
        <TableCell className={classes.cell}>
          {item.driver?.fio || ''}{' '}
          {`${item.driver?.phone ? `+${item.driver.phone}` : ''}` || '-'}
        </TableCell>
        <TableCell className={classes.cell}>{item.comments?.driver?.text}</TableCell>
        {isCreateOrder && (
          <TableCell className={classes.cell}>{item.author?.roles[0] === 'ROLE_ORDER_OPERATOR' ? item.author?.fio : '-'}</TableCell>
        )}
        <TableCell className={classes.cell}>
          {selectedId !== item.id ? (
            <ColorButton
              color='#DDDDDD'
              onClick={() => selectAction(item.id)}
              disable={Boolean(isBuyer)}
            >
              {translates[item.status]}
            </ColorButton>
          ) : (
            <TextField
              select
              margin='none'
              value={item.status}
              onChange={event => {
                handleEditStatus(event.target.value);
              }}
              variant='outlined'
              className={classes.typeList}
              size='medium'
            >
              {filteredOrderTypes &&
                filteredOrderTypes.map(option => (
                  <MenuItem key={option.type} value={option.type}>
                    {option.translate}
                  </MenuItem>
                ))}
            </TextField>
          )}
        </TableCell>
        <TableCell className={classes.cell}>
          {/* <IconButton
            onClick={() => {
              editAction(item, ViewMode.VIEW);
            }}
          >
            <VisibilityIcon color='primary' />
          </IconButton> */}

          {isEditOrder && (
            <>
              {item.status !== 'formed' && (
                <IconButton
                  color='primary'
                  aria-label='previous'
                  onClick={() => {
                    editAction(item, ViewMode.EDIT);
                  }}
                >
                  <EditIcon />
                </IconButton>
              )}

              {!isOperator && (
                <IconButton
                  onClick={() => {
                    handleDeleteDialog(item.id);
                  }}
                >
                  <DeleteIcon color='error' />
                </IconButton>
              )}
            </>
          )}
        </TableCell>
      </TableRow>
    </>
  );
};

export default React.memo(TableRowOrderList);
